import { LinearProgress } from "@octopusdeploy/design-system-components";
import type { EnvironmentV2Resource } from "@octopusdeploy/octopus-server-client";
import { AccountType, mapFromEnvironmentResourceToEnvironmentV2Resource, Permission, Repository } from "@octopusdeploy/octopus-server-client";
import { useTrackEvent } from "@octopusdeploy/portal-analytics";
import * as React from "react";
import { useAnalyticTrackedActionDispatch, useCorrelationId } from "~/analytics/Analytics";
import { AccountEditInternal, type AccountInitialData } from "~/areas/infrastructure/components/AccountEdit/AccountPage";
import { useEphemeralEnvironmentsEnabled } from "~/areas/projects/components/EphemeralEnvironments/useEphemeralEnvironmentsEnabled";
import { repository } from "~/clientInstance";
import DataLoader from "~/components/DataLoader";
import PermissionCheck, { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import type { AccountDrawerControlProps } from "./AccountEditBase";
interface AccountDrawerProps extends AccountDrawerControlProps {
    newAccountType: AccountType;
}
const AccountDrawerDataLoader = DataLoader<AccountInitialData>();
export function AccountEditDrawer({ newAccountType, isOpen, setIsOpen, onSuccess: onSave }: AccountDrawerProps) {
    const trackAction = useAnalyticTrackedActionDispatch();
    const trackEvent = useTrackEvent();
    const correlationId = useCorrelationId();
    const ephemeralEnvironmentsAreEnabled = useEphemeralEnvironmentsEnabled();
    const loadData = async (): Promise<AccountInitialData> => {
        let environments: EnvironmentV2Resource[] = [];
        if (ephemeralEnvironmentsAreEnabled) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const environmentsV2Response = await repository.Environments.getV2({ spaceId: repository.spaceId!, skip: 0, take: Repository.takeAll, type: ["Static", "Parent"] });
            environments = environmentsV2Response.Environments.Items;
        }
        else {
            const environmentsResponse = await repository.Environments.all();
            environments = environmentsResponse.map(mapFromEnvironmentResourceToEnvironmentV2Resource);
        }
        const tenants = isAllowed({ permission: Permission.TenantView, tenant: "*" }) ? repository.Tenants.all() : Promise.resolve([]);
        const azureEnvironments = repository.Accounts.getIsolatedAzureEnvironments();
        return {
            environments: environments,
            tenants: await tenants,
            azureEnvironments: await azureEnvironments,
            accountType: newAccountType,
        };
    };
    if (!isOpen) {
        return null;
    }
    return (<PermissionCheck permission={Permission.AccountCreate} environment="*" tenant="*" key="perms">
            <AccountDrawerDataLoader load={loadData} renderWhenLoaded={function (data: AccountInitialData): React.ReactNode {
            return (newAccountType !== AccountType.None && (<AccountEditInternal initialData={{ ...data, accountType: newAccountType }} trackAction={trackAction} trackEvent={trackEvent} correlationId={correlationId} type="drawer" isOpen={isOpen} setIsOpen={setIsOpen} onSuccess={onSave}></AccountEditInternal>));
        }} renderAlternate={({ busy, errors }) => <LinearProgress show={busy} variant="indeterminate"></LinearProgress>}></AccountDrawerDataLoader>
        </PermissionCheck>);
}
